import { useState } from 'react'
import { observer } from 'mobx-react-lite'
import Button from '../Button'
import { CallRailForm } from './setup-forms/CallRailForm'
import { AscendWorkspaceModalForm } from './setup-forms/AscendWorkspaceModalForm'
import { CarestackWorkspaceModalForm } from './setup-forms/CarestackWorkspaceModalForm'
import { FacebookWorkspaceModalForm } from './setup-forms/FacebookWorkspaceModalForm'
import { GravityFormWorkspaceModal } from './setup-forms/GravityFormWorkspaceModal'
import { JotformWorkspaceForm } from './setup-forms/JotformWorkspaceForm'
import { RingCentralForm } from './setup-forms/RingCentralForm'
import { IntegrationCard } from 'app/components/integrations/IntegrationCard'
import { TwilioForm } from 'app/components/integrations/setup-forms/TwilioForm'
import { useFeatureToggle } from 'app/hooks/useFeatureToggle'
import { useIsOrgUser } from 'app/hooks/useIsOrgUser'
import { closeModal, showModal } from 'app/lib/modal'
import { IntegrationsRow } from 'app/types/integrations'
import { hasRole, useUser, useWorkspace } from 'app/utils'
import { isSMCOrg } from 'lib/isSMCOrg'
import { log } from 'lib/logging'

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const AddNewWorkspaceIntegrationModal = observer(function AddNewWorkspaceIntegrationModal({
  onIntegrationChanged,
}: {
  onIntegrationChanged: () => void
}) {
  const [integration, setIntegration] = useState<IntegrationsRow[`integration`] | undefined>(undefined)
  const workspace = useWorkspace()
  const user = useUser()
  const staffV2Enabled = useFeatureToggle(`staff-v2`)
  const isOrgUser = useIsOrgUser()

  // Permission-based check with legacy fallback for org user
  const userIsOrg = staffV2Enabled ? isOrgUser : hasRole(user!, `orgUser`)

  const onNextClick = () => {
    closeModal()

    switch (integration) {
      case `CallRail`:
        showModal(
          <CallRailForm onIntegrationChanged={onIntegrationChanged} />,
          {
            title: `CallRail Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Carestack`:
        showModal(
          <CarestackWorkspaceModalForm onIntegrationChanged={onIntegrationChanged} />,
          {
            title: `Carestack Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Ascend`:
        showModal(
          <AscendWorkspaceModalForm onIntegrationChanged={onIntegrationChanged} />,
          {
            title: `Ascend Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `RingCentral`:
        showModal(
          <RingCentralForm onIntegrationChanged={onIntegrationChanged} />,
          {
            title: `RingCentral Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Twilio`:
        showModal(
          <TwilioForm onIntegrationChanged={onIntegrationChanged} />,
          {
            title: `Twilio Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Facebook Ads`:
        showModal(
          <FacebookWorkspaceModalForm onIntegrationChanged={onIntegrationChanged} integrationType="facebook-ads" />,
          {
            title: `Facebook Ads Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Facebook Leads`:
        showModal(
          <FacebookWorkspaceModalForm onIntegrationChanged={onIntegrationChanged} integrationType="facebook-leads" />,
          {
            title: `Facebook Leads Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Gravity Forms`:
        showModal(
          <GravityFormWorkspaceModal onIntegrationChanged={onIntegrationChanged} />,
          {
            title: `Gravity Forms Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      case `Jotform`:
        showModal(
          <JotformWorkspaceForm onIntegrationChanged={onIntegrationChanged} orgId={user?.orgId} />,
          {
            title: `JotForm Workspace Integration`,
            isFullScreen: false,
            overflowHidden: false,
            removePadding: true,
            useRadix: true,
          },
          false,
        )
        break
      default:
        log(`Integration not found`)
        break
    }
  }

  return (
    <div className="flex max-w-[80vw] flex-col items-center justify-center overflow-hidden bg-surface-muted">
      <div className="inline-flex flex-wrap items-center justify-center gap-4 p-6">
        {userIsOrg && (
          <>
            <IntegrationCard
              name="Carestack"
              id="Carestack"
              icon="/public/carestack.png"
              onClick={() => setIntegration(`Carestack`)}
              currentSelection={integration}
            />
            <IntegrationCard
              name="Ascend"
              id="Ascend"
              icon="/public/ascend.png"
              onClick={() => setIntegration(`Ascend`)}
              currentSelection={integration}
            />
            <IntegrationCard
              name="RingCentral"
              id="RingCentral"
              icon="/public/ringcentral.png"
              onClick={() => setIntegration(`RingCentral`)}
              currentSelection={integration}
            />
            <IntegrationCard
              name="CallRail"
              id="CallRail"
              icon="/public/callrail.png"
              onClick={() => setIntegration(`CallRail`)}
              currentSelection={integration}
            />
            <IntegrationCard
              name="Twilio"
              id="Twilio"
              icon="/public/twilio.png"
              onClick={() => setIntegration(`Twilio`)}
              currentSelection={integration}
            />
            <IntegrationCard
              name="Gravity Forms"
              id="Gravity Forms"
              icon="/public/gravity.png"
              onClick={() => setIntegration(`Gravity Forms`)}
              currentSelection={integration}
              matIcon
            />
            <IntegrationCard
              name="Jotform"
              id="Jotform"
              icon="/public/jotform.png"
              onClick={() => setIntegration(`Jotform`)}
              currentSelection={integration}
              matIcon
            />
          </>
        )}

        {!isSMCOrg(workspace?.orgId) && (
          <>
            <IntegrationCard
              name="Facebook Ads"
              id="Facebook Ads"
              icon="/public/meta_icon.png"
              onClick={() => setIntegration(`Facebook Ads`)}
              currentSelection={integration}
            />
          </>
        )}

        <IntegrationCard
          name="Facebook Leads"
          id="Facebook Leads"
          icon="/public/meta_icon.png"
          onClick={() => setIntegration(`Facebook Leads`)}
          currentSelection={integration}
        />
      </div>
      <div className="sticky bottom-0 w-full rounded-b-lg border-y border-default bg-surface-raised p-4">
        <div className="flex justify-end gap-x-4 align-middle">
          <Button label="Cancel" mode="white" size="md" onClick={() => closeModal()} />
          <Button label="Next" mode="primary" size="md" disabled={!integration} onClick={() => onNextClick()} />
        </div>
      </div>
    </div>
  )
})
